import React from "react";
import { Collapse } from "antd";
import { Field, FormSection, formValueSelector } from "redux-form";
import { useSelector } from "react-redux";

import { CustomSwitch } from "../../../../../../../../common/customSwitch";
import styles from "../../../../../../JobStyle.module.css";
import layoutStyles from "../../../../../../../SwmsMasters/EmergencyPlans/EmergencyPlansLayout.module.css";
import JobSwmsV3ForeseeableRescueScenariosSection from "./JobSwmsV3ForeseeableRescueScenariosSection";
import JobSwmsV3EmergencyResponseProcedureSection from "./JobSwmsV3EmergencyResponseProcedureSection";
import RolesResponsibilitiesCompetencySection from "../../../../../../../SwmsMasters/EmergencyPlans/sections/section-types/RolesResponsibilitiesCompetencySection";
import AlarmCommunicationEmergencyAccessSection from "../../../../../../../SwmsMasters/EmergencyPlans/sections/section-types/AlarmCommunicationEmergencyAccessSection";
import MedicalConsiderationsSection from "../../../../../../../SwmsMasters/EmergencyPlans/sections/section-types/MedicalConsiderationsSection";
import PostRescueNotificationSection from "../../../../../../../SwmsMasters/EmergencyPlans/sections/section-types/PostRescueNotificationSection";
import JobSwmsV3RescueEquipmentStagingSection from "../../../sections/default/JobSwmsV3RescueEquipmentStagingSection";
import JobSwmsV3EmergencyContactsSection from "../../../sections/default/JobSwmsV3EmergencyContactsSection";
import { resolveEmergencyPlanSectionHeading } from "../../../../../../../SwmsMasters/EmergencyPlans/sections/registry/EmergencyPlanSectionRegistry.config";

const { Panel } = Collapse;
const FORM_NAME = "JobSwmsV3EmergencyPlansContainerForm";
const formSelector = formValueSelector(FORM_NAME);
const countSelectedItemIds = (selectedItems) => {
  if (Array.isArray(selectedItems)) {
    return new Set(selectedItems.map((itemId) => String(itemId))).size;
  }

  if (selectedItems && typeof selectedItems === "object") {
    return Object.keys(selectedItems).filter((itemId) => selectedItems[itemId]).length;
  }

  return 0;
};

const countPartSelectedItems = (part = {}) => {
  if (part.selected_items !== undefined) {
    return countSelectedItemIds(part.selected_items);
  }

  return Array.isArray(part.method_items) ? part.method_items.length : 0;
};

export default function JobSwmsV3EmergencyPlanReorderableSection({
  sectionKey,
  section,
  planIndex,
  procedures,
  loading,
  methodItemTypeGroups = [],
  methodItemTypesLoading = false,
  onDragStart,
  onDragEnd,
  onDragEnter,
  onDragOver
}) {
  const heading = resolveEmergencyPlanSectionHeading(sectionKey, section);
  const sectionData = section && section.data ? section.data : (section || {});
  const formItems = useSelector((state) => formSelector(
    state,
    `plans[${planIndex}].sections.${sectionKey}.data.items`
  ));
  const formPhases = useSelector((state) => formSelector(state, `plans[${planIndex}].sections.${sectionKey}.data.phases`));
  const formConsiderations = useSelector((state) => formSelector(state, `plans[${planIndex}].sections.${sectionKey}.data.considerations`));
  const formTypes = useSelector((state) => formSelector(state, `plans[${planIndex}].sections.${sectionKey}.data.types`));
  const formRescueEquipmentData = useSelector((state) => formSelector(
    state,
    `plans[${planIndex}].sections.${sectionKey}.data`
  ));
  const sectionItems = Array.isArray(formItems) ? formItems : sectionData.items;
  const considerations = Array.isArray(formConsiderations) ? formConsiderations : sectionData.considerations;
  const phaseItems = Array.isArray(formPhases) ? formPhases : sectionData.phases;
  const contactTypes = Array.isArray(formTypes) ? formTypes : sectionData.types;
  const contactCount = Array.isArray(contactTypes)
    ? contactTypes.reduce((count, type) => count + (Array.isArray(type.contacts) ? type.contacts.length : 0), 0)
    : 0;
  const rescueEquipmentData = {
    ...sectionData,
    ...(formRescueEquipmentData || {}),
    rescue_equipment: {
      ...(sectionData.rescue_equipment || {}),
      ...((formRescueEquipmentData && formRescueEquipmentData.rescue_equipment) || {})
    },
    rescue_ppe: {
      ...(sectionData.rescue_ppe || {}),
      ...((formRescueEquipmentData && formRescueEquipmentData.rescue_ppe) || {})
    },
    licences_competencies: {
      ...(sectionData.licences_competencies || {}),
      ...((formRescueEquipmentData && formRescueEquipmentData.licences_competencies) || {})
    }
  };
  const rescueEquipmentCount = ["rescue_equipment", "rescue_ppe", "licences_competencies"].reduce(
    (count, partKey) => {
      return count + countPartSelectedItems(rescueEquipmentData[partKey]);
    },
    0
  );
  const countItems = sectionKey === "medical_considerations" ? considerations : sectionItems;
  const itemCount = sectionKey === "medical_considerations"
    ? (Array.isArray(countItems) ? countItems.length : 0)
    : sectionKey === "rescue_equipment_staging"
      ? rescueEquipmentCount
    : sectionKey === "emergency_contacts"
      ? contactCount
    : sectionKey === "emergency_response_procedure"
      ? (Array.isArray(phaseItems) ? phaseItems.length : undefined)
      : ((sectionKey === "roles_responsibilities_competency"
        || sectionKey === "alarm_communication_emergency_access"
        || sectionKey === "post_rescue_notification") && Array.isArray(countItems) ? countItems.length : undefined);

  return (
    <Panel
      key={sectionKey}
      className="custom-job-swmsv3-collapse-panel"
      header={
        <div
          className={`${styles.BackgroundSectionHeading} custom-section-v2-panel-heading`}
          draggable={!loading}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          onDragEnter={onDragEnter}
          onDragOver={onDragOver}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            cursor: loading ? "default" : "grab"
          }}
        >
          <span aria-hidden="true" style={{ display: "inline-flex", color: "#362148", opacity: 0.8 }}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512" width="14" height="14">
              <path fill="currentColor" d="M40 352l48 0c22.1 0 40 17.9 40 40l0 48c0 22.1-17.9 40-40 40l-48 0c-22.1 0-40-17.9-40-40l0-48c0-22.1 17.9-40 40-40zm192 0l48 0c22.1 0 40 17.9 40 40l0 48c0 22.1-17.9 40-40 40l-48 0c-22.1 0-40-17.9-40-40l0-48c0-22.1 17.9-40 40-40zM40 320c-22.1 0-40-17.9-40-40l0-48c0-22.1 17.9-40 40-40l48 0c22.1 0 40 17.9 40 40l0 48c0 22.1-17.9 40-40 40l-48 0zM232 192l48 0c22.1 0 40 17.9 40 40l0 48c0 22.1-17.9 40-40 40l-48 0c-22.1 0-40-17.9-40-40l0-48c0-22.1 17.9-40 40-40zM40 160c-22.1 0-40-17.9-40-40L0 72C0 49.9 17.9 32 40 32l48 0c22.1 0 40 17.9 40 40l0 48c0 22.1-17.9 40-40 40l-48 0zM232 32l48 0c22.1 0 40 17.9 40 40l0 48c0 22.1-17.9 40-40 40l-48 0c-22.1 0-40-17.9-40-40l0-48c0-22.1 17.9-40 40-40z" />
            </svg>
          </span>
          <span>{heading}</span>
          {typeof itemCount === "number" ? (
            <span
              className={layoutStyles.SectionPanelItemCountChip}
              style={{
                background: "#E7E0FF",
                color: "#4B2C79"
              }}
              title={`${itemCount} item${itemCount === 1 ? "" : "s"}`}
              aria-label={`${itemCount} item${itemCount === 1 ? "" : "s"}`}
            >
              {itemCount}
            </span>
          ) : null}
          <span
            className="float-right position-relative"
            style={{ marginLeft: "auto", marginRight: "30px" }}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <Field
              name={`plans[${planIndex}].sections.${sectionKey}.data.toggle`}
              component={CustomSwitch}
              size="small"
              disabled={loading}
            />
          </span>
        </div>
      }
    >
      {sectionKey === "foreseeable_rescue_scenarios" ? (
        <JobSwmsV3ForeseeableRescueScenariosSection
          procedures={procedures}
          planIndex={planIndex}
          loading={loading}
        />
      ) : sectionKey === "emergency_response_procedure" ? (
        <FormSection
          component={JobSwmsV3EmergencyResponseProcedureSection}
          name={`plans[${planIndex}].sections.${sectionKey}.data`}
          sectionKey={sectionKey}
          loading={loading}
        />
      ) : sectionKey === "rescue_equipment_staging" ? (
        <FormSection
          component={JobSwmsV3RescueEquipmentStagingSection}
          name={`plans[${planIndex}].sections.${sectionKey}.data`}
          sectionKey={sectionKey}
          planIndex={planIndex}
          sectionData={section}
          loading={loading}
          methodItemTypeGroups={methodItemTypeGroups}
          methodItemTypesLoading={methodItemTypesLoading}
        />
      ) : sectionKey === "roles_responsibilities_competency" ? (
        <FormSection
          component={RolesResponsibilitiesCompetencySection}
          name={`plans[${planIndex}].sections.${sectionKey}.data`}
          sectionKey={sectionKey}
          loading={loading}
          historyItemKey="_history_id"
        />
      ) : sectionKey === "medical_considerations" ? (
        <FormSection
          component={MedicalConsiderationsSection}
          name={`plans[${planIndex}].sections.${sectionKey}.data`}
          sectionKey={sectionKey}
          loading={loading}
          dragHandleOnly
        />
      ) : sectionKey === "post_rescue_notification" ? (
        <FormSection
          component={PostRescueNotificationSection}
          name={`plans[${planIndex}].sections.${sectionKey}.data`}
          sectionKey={sectionKey}
          loading={loading}
          dragHandleOnly
        />
      ) : sectionKey === "emergency_contacts" ? (
        <FormSection
          component={JobSwmsV3EmergencyContactsSection}
          name={`plans[${planIndex}].sections.${sectionKey}.data`}
          sectionKey={sectionKey}
          planIndex={planIndex}
          sectionData={section}
          loading={loading}
        />
      ) : sectionKey === "alarm_communication_emergency_access" ? (
        <FormSection
          component={AlarmCommunicationEmergencyAccessSection}
          name={`plans[${planIndex}].sections.${sectionKey}.data`}
          sectionKey={sectionKey}
          loading={loading}
          dragHandleOnly
        />
      ) : null}
    </Panel>
  );
}
